'use client';
import { type Brand } from '@curology/ui-components-web/brand';
import { Button } from '@curology/ui-components-web/react';
import { CrossIcon } from '@curology/ui-components-web/react/Icon/icons/CrossIcon';
import { SelectInput } from '@curology/ui-components-web/react/SelectInput';
import { SelectOption } from '@curology/ui-components-web/react/SelectInput/SelectOption';
import { TextInput } from '@curology/ui-components-web/react/TextInput';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useEffect, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import {
  getSuggestions,
  splitAddressStringIntoParts,
} from '@/components/checkout/js/smarty';
import { type ExpressCurologySignUpSkinPdpSourceConfigType } from '@/components/flow/expressCurologySignUpSkin/types';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { ALL_STATES, type StateShortNameType } from '@/consts/states';
import {
  type TreatmentCategory,
  TreatmentCategoryIds,
} from '@/consts/treatmentCategories';
import { NAME_REGEX, ZIP_CODE_REGEX } from '@/consts/validation';
import { extractPatientData } from '@/helpers/productSelectionAndCheckout/helpers';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import {
  saveProductEligibility,
  updateRegistrationEligibility,
} from '@/services/eligibilityService/actions';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';
import { editSessionBag } from '@/services/visitsService/client';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';
import {
  type SmartySuggestion,
  type Patient,
} from '@/types/productSelectionAndCheckout/types';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';
import cookie from '@/utils/cookies/client';

import { useNavigation } from '../../../hooks/useNavigation';

interface EventHandlerEvent {
  target: { value: React.SetStateAction<string> };
}

const trackConversion = (
  brand: Brand,
  invoiceId: string | number,
  referrerSurveyResponse: string | null,
  revenue: string,
  treatmentCategory: TreatmentCategory,
  user: Patient,
) => {
  const products =
    user?.subscriptionItems?.map((subscriptionItem) => ({
      id: subscriptionItem.itemId,
      name: subscriptionItem.name,
    })) ?? [];

  trackEvent(
    AnalyticsEvent.ORDER_COMPLETED,
    {
      currency: 'USD',
      flow: FlowTypes.PDP_CHECKOUT,
      funnelId: cookie.get(COOKIE_KEYS.funnelId),
      invoiceId,
      patient: extractPatientData(user),
      products,
      referrerSurveyResponse,
      revenue,
      treatmentCategories: treatmentCategory,
      treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory],
    },
    {
      gtm: {
        spreadProperties: true,
      },
    },
  );

  const capitalizedBrandName = brand[0].toUpperCase() + brand.substring(1);

  trackEvent(
    AnalyticsEvent.GOOGLE_ANALYTICS_TRANSACTION,
    {
      flow: FlowTypes.PDP_CHECKOUT,
      transactionId: invoiceId,
      transactionProducts: [
        {
          name: `${capitalizedBrandName} Subscription`,
          price: revenue,
          quantity: 1,
          sku: '1',
        },
      ],
      transactionTotal: revenue,
    },
    {
      gtm: {
        spreadProperties: true,
      },
    },
  );
};

// TODO: Not adding the AnalyticsEvent.FIELD_ENTERED event for now. Seems odd and unused.
export const ShippingAddressForm = ({
  brand,
  bundleConfig,
  patient,
  selectedAddOnProductItemIds,
  treatmentCategory,
  uuid,
}: {
  brand: Brand;
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType;
  patient: AnalyticsPatientResource;
  selectedAddOnProductItemIds: number[];
  treatmentCategory: TreatmentCategory;
  uuid: string;
}) => {
  const [firstName, setFirstName] = useState(patient.firstname || '');
  const [lastName, setLastName] = useState(patient.lastname || '');
  const [streetAddress, setStreetAddress] = useState('');
  const [suggestions, setSuggestions] = useState<SmartySuggestion[]>([]);
  const [apartmentSuiteEtc, setApartmentSuiteEtc] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const flashMessages = useFlashMessages();
  const { handleIfSessionExpired } = useSessionExpired(brand);
  const { onNext } = useNavigation();

  useEffect(() => {
    setCanSubmit(
      NAME_REGEX.test(firstName.trim()) &&
        NAME_REGEX.test(lastName.trim()) &&
        streetAddress.trim() !== '' &&
        city.trim() !== '' &&
        state.trim() !== '' &&
        ZIP_CODE_REGEX.test(zipCode),
    );
  }, [firstName, lastName, streetAddress, city, state, zipCode]);

  const submitForm = async () => {
    setIsSubmitting(true);

    try {
      const selectedItemIds = [
        ...bundleConfig.setProducts
          .map((setProduct) => setProduct.itemIds)
          .flat(),
        ...selectedAddOnProductItemIds,
      ];

      await ProductSelectionAndCheckoutService.updateSelectedItems(
        brand,
        selectedItemIds,
        treatmentCategory,
      );

      const data = {
        address1: streetAddress,
        address2: apartmentSuiteEtc,
        addressee_name: `${firstName} ${lastName}`,
        city: city,
        firstname: firstName,
        lastname: lastName,
        phoneNumberOptional: true,
        state: state,
        zipcode: zipCode,
      };

      const {
        data: { addressValidation },
      } = await ProductSelectionAndCheckoutService.updateShippingAddress(
        brand,
        data,
      );

      await saveProductEligibility({
        brand,
        militaryAddress: addressValidation?.isMilitaryAddress,
        treatmentCategories: treatmentCategory,
      });

      await updateRegistrationEligibility(brand, {
        state,
        treatmentCategories: treatmentCategory,
      });

      const shipmentFrequencyDays = 60;

      const {
        data: {
          data: { invoiceId, referrerSurveyResponse, revenue, user },
        },
      } = await ProductSelectionAndCheckoutService.activateTrial(
        brand,
        shipmentFrequencyDays,
      );

      await editSessionBag({
        brand,
        sessionBag: {
          hasActivatedTrial: true,
          hasAddedShippingAddress: true,
        },
        uuid,
      });

      flashMessages.success({
        title: 'Order confirmed!',
        message:
          'Now answer some quick questions to help your provider personalize your formula.',
      });

      trackConversion(
        brand,
        invoiceId,
        referrerSurveyResponse,
        revenue.toFixed(2),
        treatmentCategory,
        user,
      );

      trackEvent(AnalyticsEvent.CHECKOUT_CONTINUE_BUTTON_CLICKED, {
        card: 'address',
        flow: FlowTypes.PDP_CHECKOUT,
      });

      onNext();
    } catch (e: any) {
      setIsSubmitting(false);

      if (handleIfSessionExpired(e.response?.status)) return;

      const errorMessages = Object.values(
        e.response?.data?.errors ?? {},
      ).flat();

      if (errorMessages.length > 0) {
        errorMessages.forEach((errorMessage) => {
          flashMessages.error({
            title: 'Sorry!',
            message: String(errorMessage),
          });
        });
      } else {
        flashMessages.error({
          title: 'Sorry!',
          message: 'A system error occurred. Please try again.',
        });
      }
    }
  };

  return (
    <form>
      <div className="cur-mb-4">
        <TextInput
          disabled={isSubmitting}
          id="firstName"
          label="First name"
          onChange={(event: EventHandlerEvent) =>
            setFirstName(event.target.value)
          }
          placeholder="Jane"
          required
          value={firstName}
        />
      </div>
      <div className="cur-mb-4">
        <TextInput
          disabled={isSubmitting}
          id="lastName"
          label="Last name"
          onChange={(event: EventHandlerEvent) =>
            setLastName(event.target.value)
          }
          placeholder="Doe"
          required
          value={lastName}
        />
      </div>
      <div className="cur-mb-4">
        <TextInput
          disabled={isSubmitting}
          id="streetAdddress"
          label="Street address"
          onChange={(event: EventHandlerEvent) => {
            setStreetAddress(event.target.value);
            getSuggestions(brand, String(event.target.value), setSuggestions);
          }}
          placeholder="1234 Main Street"
          required
          value={streetAddress}
        />
        {suggestions.length > 0 && (
          <div className="cur-relative data-hj-suppress">
            <div className="cur-absolute cur-bg-surface-neutral cur-border cur-border-disabled cur-p-3 cur-top-[-1px] cur-w-full cur-z-10">
              <div className="cur-flex cur-items-center cur-justify-between">
                <Typography className="cur-text-disabled">
                  Suggestions
                </Typography>
                <Button
                  buttonType="text"
                  data-sentry-id="Close address pre-fill suggestions"
                  iconStart={CrossIcon}
                  onClick={() => setSuggestions([])}
                  size="sm"
                  state="dark"
                />
              </div>
              <ul className="cur-mb-0 cur-mt-3">
                {suggestions.map((suggestion, index) => {
                  const {
                    city: newCity,
                    state: newState,
                    street_line: newStreetAddress,
                    zipcode: newZipCode,
                  } = suggestion;

                  const searchString = streetAddress;
                  const addressString = `${newStreetAddress}, ${newCity}, ${newState} ${newZipCode}`;

                  const addressStringParts = splitAddressStringIntoParts(
                    searchString,
                    addressString,
                  );

                  return (
                    <li key={index}>
                      <button
                        className="cur-overflow-hidden cur-py-2 cur-text-ellipsis cur-text-left cur-w-full cur-whitespace-nowrap"
                        data-sentry-id="Select address pre-fill suggestion"
                        onClick={() => {
                          setStreetAddress(newStreetAddress);
                          setCity(newCity);
                          setState(newState);
                          setZipCode(newZipCode);

                          setSuggestions([]);

                          trackEvent(
                            AnalyticsEvent.ADDRESS_SUGGESTION_SELECTED,
                            {
                              flow: FlowTypes.PDP_CHECKOUT,
                            },
                          );
                        }}
                        type="button"
                      >
                        {addressStringParts.map((addressStringPart, index) => {
                          return (
                            <Typography
                              className="cur-inline"
                              key={index}
                              weight={
                                addressStringPart.style === 'bold'
                                  ? 'bold'
                                  : 'normal'
                              }
                            >
                              {addressStringPart.substring}
                            </Typography>
                          );
                        })}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </div>
      <div className="cur-mb-4">
        <TextInput
          disabled={isSubmitting}
          id="apartmentSuiteEtc"
          label="Apartment, suite, etc."
          onChange={(event: EventHandlerEvent) =>
            setApartmentSuiteEtc(event.target.value)
          }
          placeholder="Apt 100"
          value={apartmentSuiteEtc}
        />
      </div>
      <div className="cur-mb-4">
        <TextInput
          disabled={isSubmitting}
          id="city"
          label="City"
          onChange={(event: EventHandlerEvent) => setCity(event.target.value)}
          placeholder="San Francisco"
          required
          value={city}
        />
      </div>
      <div className="cur-flex cur-items-center cur-mb-4 -cur-mr-4">
        <div className="cur-flex-1 cur-pr-4">
          <SelectInput
            id="state"
            label="State"
            onChange={(event: EventHandlerEvent) =>
              setState(event.target.value)
            }
            required
            value={state}
          >
            {Object.keys(ALL_STATES).map((key) => {
              return (
                <SelectOption key={key} value={key}>
                  {ALL_STATES[key as StateShortNameType]}
                </SelectOption>
              );
            })}
          </SelectInput>
        </div>
        <div className="cur-flex-1 cur-pr-4">
          <TextInput
            disabled={isSubmitting}
            id="zipCode"
            label="Zip code"
            onChange={(event: EventHandlerEvent) =>
              setZipCode(event.target.value)
            }
            placeholder="92104"
            required
            value={zipCode}
          />
        </div>
      </div>
      <div>
        <Button
          className="cur-w-full"
          disabled={!canSubmit || isSubmitting}
          onClick={submitForm}
          type="button"
        >
          Submit payment
        </Button>
      </div>
    </form>
  );
};
